import gql from 'graphql-tag';
import { useMemo } from 'react';

import { getIsRecognizedProvider } from '@/app/integrations/providerUtil';
import { FEATURE_FLAG } from '@/constants/featureFlags';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useSolImmutableQuery, useSolQuery } from '@/hooks/useSolQuery';

import { fetcherSol } from '../sol-fetcher';
import {
  INTEGRATION_PROVIDER_ID,
  type IntegrationConnectionAPIKeyCreateGQLResponse,
  type IntegrationConnectionAPIKeyCreateVariablesType,
  type IntegrationConnectionCreateGQLResponse,
  type IntegrationConnectionCreateVariablesType,
  type IntegrationConnectionDeleteGQLResponse,
  type IntegrationConnectionDeleteVariablesType,
  type IntegrationConnectionDetailGQLResponse,
  type IntegrationProviderCapability,
  type IntegrationProviderListGQLResponse,
  type IntegrationProviderType,
  type IntegrationTokenCreateGQLResponse,
  type IntegrationTokenCreateVariablesType,
} from './integrationType';

const normalizeIntegrationProvidersFromResponse = (
  data: IntegrationProviderListGQLResponse | undefined,
  isStackAdaptEnabled: boolean,
): IntegrationProviderType[] | undefined => {
  if (!data) {
    return undefined;
  }

  return data.oAuthProviders.get.edges
    .map((d) => ({
      ...d.node,
      available: true,
      connections: d.node.connections.edges.map((conn) => conn.node),
    }))
    .filter((provider) => {
      if (!getIsRecognizedProvider(provider.id)) {
        return false;
      }

      if (provider.id === INTEGRATION_PROVIDER_ID.STACK_ADAPT && !isStackAdaptEnabled) {
        return false;
      }

      return true;
    })
    .sort((a, b) => a.name.localeCompare(b.name));
};

export const useIntegrationProviders = () => {
  const isStackAdaptEnabled = useFeatureFlag(FEATURE_FLAG.STACK_ADAPT);
  const { data, isLoading, error, mutate } =
    useSolImmutableQuery<IntegrationProviderListGQLResponse>({
      query: gql`
        query oAuthProviders(
          $page: Pagination!
          $sort: [SortParamInput]!
          $filter: [FilterParamInput]!
          $connPage: Pagination!
          $connSort: [SortParamInput]!
          $connFilter: [FilterParamInput]!
        ) {
          oAuthProviders {
            get(page: $page, sort: $sort, filter: $filter) {
              edges {
                node {
                  id
                  name
                  category
                  capabilities {
                    id
                    settings {
                      name
                      stringValue
                    }
                  }
                  startUrl
                  type
                  multiConnection
                  syncIntervalHours
                  connections(page: $connPage, sort: $connSort, filter: $connFilter) {
                    edges {
                      node {
                        id
                        createdBy {
                          email
                          name
                        }
                        lastJob {
                          created_at
                          status
                          errors
                        }
                        organizationId
                        organizationName
                        status
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `,
      variables: {
        page: {
          limit: 20,
          offset: 0,
        },
        sort: [
          {
            direction: 'asc',
            field: 'name',
          },
        ],
        filter: [],
        connPage: {
          limit: 100,
          offset: 0,
        },
        connSort: [],
        connFilter: [],
      },
    });

  const providers = useMemo(
    () => normalizeIntegrationProvidersFromResponse(data, isStackAdaptEnabled),
    [data, isStackAdaptEnabled],
  );

  return {
    providers,
    isLoading,
    error,
    mutate,
  };
};

export const useIntegrationProvidersLite = () => {
  const isStackAdaptEnabled = useFeatureFlag(FEATURE_FLAG.STACK_ADAPT);
  const { data, isLoading, error, mutate } =
    useSolImmutableQuery<IntegrationProviderListGQLResponse>({
      query: gql`
        query oAuthProvidersLite(
          $page: Pagination!
          $sort: [SortParamInput]!
          $filter: [FilterParamInput]!
          $connPage: Pagination!
          $connSort: [SortParamInput]!
          $connFilter: [FilterParamInput]!
        ) {
          oAuthProviders {
            get(page: $page, sort: $sort, filter: $filter) {
              edges {
                node {
                  id
                  name
                  category
                  capabilities {
                    id
                    settings {
                      name
                      stringValue
                    }
                  }
                  type
                  connections(page: $connPage, sort: $connSort, filter: $connFilter) {
                    edges {
                      node {
                        id
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `,
      variables: {
        page: {
          limit: 20,
          offset: 0,
        },
        sort: [
          {
            direction: 'asc',
            field: 'name',
          },
        ],
        filter: [],
        connPage: {
          limit: 1,
          offset: 0,
        },
        connSort: [],
        connFilter: [],
      },
    });

  const providers = useMemo(
    () => normalizeIntegrationProvidersFromResponse(data, isStackAdaptEnabled),
    [data, isStackAdaptEnabled],
  );

  return {
    providers,
    isLoading,
    error,
    mutate,
  };
};

export const useIntegrationConnectionHealthChecks = (connId: string) => {
  const { data, isLoading, error } = useSolQuery<IntegrationConnectionDetailGQLResponse>({
    query: gql`
      query GetOAuthConnectionById($connId: String!) {
        oAuthConnections {
          getById(id: $connId) {
            healthChecks {
              description
              details
              name
              type
            }
          }
        }
      }
    `,
    variables: {
      connId,
    },
  });

  return {
    healthChecks: data?.oAuthConnections.getById.healthChecks,
    isLoading,
    error,
  };
};

export const useIntegrationCRMProviders = () => {
  const { providers, isLoading, error, mutate } = useIntegrationProvidersLite();

  const hasHubspotConnection = useMemo(() => {
    return providers?.some(
      (d) => d.id === INTEGRATION_PROVIDER_ID.HUBSPOT && d.connections.length > 0,
    );
  }, [providers]);

  const hasSalesforceConnection = useMemo(() => {
    return providers?.some(
      (d) => d.id === INTEGRATION_PROVIDER_ID.SALESFORCE && d.connections.length > 0,
    );
  }, [providers]);

  return {
    providers: providers?.filter((d) => d.category === 'crm'),
    hasHubspotConnection,
    hasSalesforceConnection,
    isLoading,
    error,
    mutate,
  };
};

export const useIntegrationProvidersByCapability = (capability: IntegrationProviderCapability) => {
  const { providers, isLoading, error, mutate } = useIntegrationProvidersLite();

  const providersByCapability = useMemo(
    () =>
      providers?.filter(
        (d) => d.connections.length > 0 && d.capabilities.some((c) => c.id === capability),
      ),
    [providers, capability],
  );

  return {
    providers: providersByCapability,
    isLoading,
    error,
    mutate,
  };
};

export const useIntegrationProviderDetails = (providerId: INTEGRATION_PROVIDER_ID) => {
  const { providers, isLoading, error, mutate } = useIntegrationProviders();
  const provider = providers?.find((d) => d.id === providerId);

  let is404 = false;
  if (providers && !provider) {
    is404 = true;
  }

  return {
    provider,
    is404,
    isLoading,
    error,
    mutate,
  };
};

export const mutateSaveIntegrationConnectionState = async (state: string) => {
  const response = await fetcherSol<
    { state: string },
    { oAuthConnections: { saveState: { success: boolean } } }
  >({
    query: gql`
      mutation SaveIntegrationConnectionState($state: String!) {
        oAuthConnections {
          saveState(state: $state) {
            success
          }
        }
      }
    `,
    variables: {
      state,
    },
  });

  return response.oAuthConnections.saveState.success;
};

export const mutateIntegrationTokenCreate = async (
  oAuthProviderId: string,
  state: string,
  params: URLSearchParams,
) => {
  const response = await fetcherSol<
    IntegrationTokenCreateVariablesType,
    IntegrationTokenCreateGQLResponse
  >({
    query: gql`
      mutation OAuthTokenCreate(
        $oAuthProviderId: String!
        $state: String!
        $autoCreate: Boolean
        $params: [OAuthQueryParamInput]
      ) {
        oAuthTokens {
          createWithState(
            oAuthProviderId: $oAuthProviderId
            state: $state
            autoCreate: $autoCreate
            params: $params
          ) {
            id
            orgs {
              id
              name
            }
            connection {
              id
            }
          }
        }
      }
    `,
    variables: {
      oAuthProviderId,
      state,
      autoCreate: true,
      params: [...params.entries()].map(([name, value]) => ({ name, value })),
    },
  });

  return response.oAuthTokens.createWithState;
};

export const mutateIntegrationConnectionCreate = async (
  oAuthTokenId: string,
  organizationId: string,
  state: string,
) => {
  const response = await fetcherSol<
    IntegrationConnectionCreateVariablesType,
    IntegrationConnectionCreateGQLResponse
  >({
    query: gql`
      mutation OAuthConnectionCreate(
        $oAuthTokenId: String!
        $organizationId: String!
        $state: String!
      ) {
        oAuthConnections {
          createWithState(
            oAuthTokenId: $oAuthTokenId
            organizationId: $organizationId
            state: $state
          ) {
            id
          }
        }
      }
    `,
    variables: {
      oAuthTokenId,
      organizationId,
      state,
    },
  });

  return response.oAuthConnections.createWithState.id;
};

export const mutateIntegrationConnectionDelete = async (oAuthConnectionId: string) => {
  const response = await fetcherSol<
    IntegrationConnectionDeleteVariablesType,
    IntegrationConnectionDeleteGQLResponse
  >({
    query: gql`
      mutation DeleteOAuthConnection($id: String!) {
        oAuthConnections {
          delete(id: $id) {
            success
            id
          }
        }
      }
    `,
    variables: {
      id: oAuthConnectionId,
    },
  });

  return response.oAuthConnections.delete.success;
};

export const mutateIntegrationConnectionAPIKeyCreate = async (
  oAuthProviderId: string,
  apiKeys: Record<string, string>,
) => {
  const response = await fetcherSol<
    IntegrationConnectionAPIKeyCreateVariablesType,
    IntegrationConnectionAPIKeyCreateGQLResponse
  >({
    query: gql`
      mutation IntegrationConnectionAPIKeyCreate($providerId: String!, $settings: JSON!) {
        oAuthConnections {
          createForProvider(providerId: $providerId, settings: $settings) {
            id
          }
        }
      }
    `,
    variables: {
      providerId: oAuthProviderId,
      settings: JSON.stringify(apiKeys),
    },
  });

  return response.oAuthConnections.createForProvider.id;
};
